import React, { useEffect, useMemo, useState } from 'react';
import { Video, MapPin } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t, formatDate, formatTime } from '../i18n';
import { dayKey } from '../lib/practiceTime';
import { sessionsInScope } from '../lib/availability';
import { Button, Modal, Field, Input, Select, Alert } from './ui';

/**
 * Switching a patient's sessions between in person and online (migration 0044).
 *
 * The patient chose when booking; afterwards it is the professional's call —
 * for one session, the next several, or every session in a range of weeks,
 * always one patient at a time. The server checks each session again and says
 * which it left alone and why.
 */

const ModalityModal = ({ patientName, sessions, initialScope, onClose, onDone }) => {
  const [kind, setKind] = useState(initialScope.kind);
  const [count, setCount] = useState(String(initialScope.count ?? sessions.length));
  const [from, setFrom] = useState(() => dayKey(sessions[0]?.starts_at ?? Date.now()));
  const [to, setTo] = useState(() => dayKey(sessions[sessions.length - 1]?.starts_at ?? Date.now()));
  const [target, setTarget] = useState('online');
  const [sedeId, setSedeId] = useState('');
  const [sedes, setSedes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    supabase.from('locations').select('id, name').eq('is_active', true).eq('is_online', false).order('name')
      .then(({ data }) => setSedes(data ?? []));
  }, []);

  const selected = useMemo(() => sessionsInScope(sessions, kind === 'one'
    ? { kind, id: initialScope.id }
    : kind === 'next' ? { kind, count } : { kind, from, to }), [sessions, kind, count, from, to, initialScope.id]);

  const apply = async () => {
    setBusy(true);
    setError(null);
    setWarning(null);
    const { data, error: changeError } = await supabase.rpc('change_session_location', {
      p_appointment_ids: selected.map((session) => session.id),
      p_online: target === 'online',
      p_location_id: target === 'in_person' && sedeId ? sedeId : null,
    });
    setBusy(false);
    if (changeError) { setError(changeError.message); return; }

    const skipped = data.skipped ?? [];
    const noSede = skipped.filter((item) => item.reason === 'no_sede').length;
    const started = skipped.filter((item) => item.reason === 'started').length;
    const lines = [t('modality.changed', { count: data.changed })];
    if (data.unchanged) lines.push(t('modality.unchanged', { count: data.unchanged }));
    if (started) lines.push(t('modality.skippedStarted', { count: started }));
    if (noSede) lines.push(t('modality.skippedNoSede', { count: noSede }));

    // Sessions left out for want of a sede can be sent again with one picked,
    // so the dialog stays open and says so.
    if (noSede) {
      onDone?.(null);
      setWarning(lines.join(' '));
      return;
    }
    onDone?.(lines.join(' '));
    onClose();
  };

  return (
    <Modal
      open
      title={t('modality.title', { name: patientName })}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={apply} loading={busy} disabled={!selected.length}>{t('modality.apply')}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('modality.which')}>
          <div className="space-y-2 text-sm text-slate-700">
            {initialScope.id && (
              <label className="flex items-center gap-2">
                <input type="radio" checked={kind === 'one'} onChange={() => setKind('one')} />
                {t('modality.one')}
              </label>
            )}
            <label className="flex flex-wrap items-center gap-2">
              <input type="radio" checked={kind === 'next'} onChange={() => setKind('next')} />
              {t('modality.next')}
              <Input type="number" min="1" max={sessions.length} value={count} className="w-20"
                onChange={(event) => { setKind('next'); setCount(event.target.value); }} />
              {t('modality.nextUnit')}
            </label>
            <label className="flex flex-wrap items-center gap-2">
              <input type="radio" checked={kind === 'weeks'} onChange={() => setKind('weeks')} />
              {t('modality.weeks')}
              <Input type="date" value={from} className="w-40"
                onChange={(event) => { setKind('weeks'); setFrom(event.target.value); }} />
              {t('modality.weeksTo')}
              <Input type="date" value={to} className="w-40"
                onChange={(event) => { setKind('weeks'); setTo(event.target.value); }} />
            </label>
          </div>
        </Field>

        <Field label={t('modality.to')}>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
            <label className="flex items-center gap-2">
              <input type="radio" checked={target === 'online'} onChange={() => setTarget('online')} />
              <Video size={14} />{t('availability.online')}
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={target === 'in_person'} onChange={() => setTarget('in_person')} />
              <MapPin size={14} />{t('availability.inPerson')}
            </label>
            {target === 'in_person' && (
              <Select value={sedeId} onChange={(event) => setSedeId(event.target.value)} className="w-64">
                <option value="">{t('modality.sedeByHours')}</option>
                {sedes.map((sede) => <option key={sede.id} value={sede.id}>{sede.name}</option>)}
              </Select>
            )}
          </div>
        </Field>

        {selected.length === 0 ? (
          <Alert tone="info">{t('modality.none')}</Alert>
        ) : (
          <div className="rounded-lg bg-slate-50 p-3 text-sm">
            <p className="font-medium text-slate-800">{t('modality.count', { count: selected.length })}</p>
            <ul className="mt-1 space-y-0.5 text-slate-600">
              {selected.slice(0, 6).map((session) => (
                <li key={session.id} className="first-letter:uppercase">
                  {formatDate(session.starts_at, { weekday: 'short', day: 'numeric', month: 'short' })}
                  {' '}{formatTime(session.starts_at)}
                  {session.locations?.name ? ` · ${session.locations.name}` : ''}
                </li>
              ))}
              {selected.length > 6 && <li>{t('modality.more', { count: selected.length - 6 })}</li>}
            </ul>
          </div>
        )}

        {warning && <Alert tone="warning">{warning}</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}
        <p className="text-xs text-slate-500">{t('modality.invitations')}</p>
      </div>
    </Modal>
  );
};

export default ModalityModal;
